import { FocusRing } from '@upright/ui/common/generic/components/focus-ring';
import { Tooltip } from '@upright/ui/common/generic/components/tooltip';
import { ForwardedRef, forwardRef, HTMLAttributes, ReactNode } from 'react';
import { mergeProps, useFocusRing } from 'react-aria';
import { Badge, type Props as BadgeProps } from './Badge';
import styles from './TooltipBadge.module.css';

export type Props = BadgeProps & {
  /** What the badge means, for a reader who does not know the term on it. */
  tooltip: ReactNode;
  /** Accessible name of the badge; the tooltip is announced as its description. */
  label: string;
};

/**
 * A badge whose meaning is explained in a tooltip, for a label that is a term of art ("Stand-in")
 * rather than plain language.
 *
 * The badge itself stays a label, not a control: no press handler and no hover fill. What the
 * tooltip adds is a tab stop, because a tooltip only a mouse can reach is not one
 * (design/50-components.md, "Focus and interaction states"), and a tab stop needs its own focus
 * ring.
 */
export function TooltipBadge({ tooltip, label, ...badgeProps }: Props) {
  return (
    <Tooltip text={tooltip} placement="top" isNonFocusableTarget>
      <BadgeSlot label={label}>
        <Badge {...badgeProps} />
      </BadgeSlot>
    </Tooltip>
  );
}

type BadgeSlotProps = HTMLAttributes<HTMLSpanElement> & { label: string; children: ReactNode };

/**
 * The element the tooltip attaches to: Badge forwards no ref, so the trigger has to be one we own.
 *
 * `Tooltip`'s `isNonFocusableTarget` makes it keyboard-reachable by stamping `tabIndex: 0` on it. A
 * non-native tab stop is outside `reset.css`, so `FocusRing` paints its ring, once `useFocusRing`
 * says the focus came from the keyboard. `role="img"` is what makes `label` a name a screen reader
 * announces, and it is also the interactive role react-aria's `Focusable` requires of its child.
 */
const BadgeSlot = forwardRef(function BadgeSlot(
  { label, children, ...slotProps }: BadgeSlotProps,
  ref: ForwardedRef<HTMLElement>,
) {
  const { isFocusVisible, focusProps } = useFocusRing();
  return (
    <FocusRing ref={ref} isFocusVisible={isFocusVisible}>
      <span {...mergeProps(slotProps, focusProps)} className={styles.slot} role="img" aria-label={label}>
        {children}
      </span>
    </FocusRing>
  );
});
